import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faInfoCircle, faTree } from '@fortawesome/free-solid-svg-icons';
import {
  CASTLE_CLOSED_UNTIL_CHRISTMAS_2026,
  isCastleClosedUntilChristmas,
} from '../../utils/openingHoursUtils';

const STORAGE_KEY = 'castleClosedUntilChristmas2026Popup';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: castleClosedFadeIn 0.3s ease-out forwards;

  @keyframes castleClosedFadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2.5rem 2rem;
  max-width: 550px;
  width: 90%;
  position: relative;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  text-align: center;
  animation: castleClosedSlideIn 0.3s ease-out forwards;

  @keyframes castleClosedSlideIn {
    from {
      transform: scale(0.8) translateY(-20px);
      opacity: 0;
    }
    to {
      transform: scale(1) translateY(0);
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    max-width: 95%;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary};
  cursor: pointer;
  font-size: 1.5rem;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
    transform: scale(1.1);
  }
`;

const Icon = styled.div`
  font-size: 3rem;
  color: ${props => props.theme.colors.accent};
  margin-bottom: 1rem;
`;

const Title = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const Subtitle = styled.p`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.2rem;
  font-weight: 600;
  color: ${props => props.theme.colors.accent};
  margin: 0 0 1.5rem;
`;

const OpenNotice = styled.div`
  background-color: #f1f8f1;
  border: 2px solid rgba(34, 139, 34, 0.35);
  border-radius: 10px;
  padding: 1rem 1.25rem;
  margin-bottom: 1.5rem;
  font-family: ${props => props.theme.fonts.main};
  color: ${props => props.theme.colors.primary};
  line-height: 1.5;

  strong {
    display: block;
    font-size: 1.2rem;
    color: #1f6b1f;
    margin-bottom: 0.25rem;
  }

  span {
    font-size: 1.05rem;
  }
`;

const ActionLink = styled(Link)`
  display: inline-block;
  background-color: ${props => props.theme.colors.accent};
  color: white;
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.1rem;
  font-weight: 600;
  padding: 0.9rem 2rem;
  border-radius: 25px;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    background-color: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

/**
 * Shown once per browser session while the castle is closed ahead of its
 * Christmas re-opening. Makes clear the Gardens and Playground stay open.
 */
const CastleClosedPopup = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!isCastleClosedUntilChristmas()) return;

    try {
      if (sessionStorage.getItem(STORAGE_KEY)) return;
    } catch (e) {
      // sessionStorage unavailable — still show the popup
    }

    setIsVisible(true);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    try {
      sessionStorage.setItem(STORAGE_KEY, 'true');
    } catch (e) {
      // ignore
    }
  };

  if (!isVisible) return null;

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent
        role="dialog"
        aria-modal="true"
        aria-labelledby="castle-closed-title"
        onClick={e => e.stopPropagation()}
      >
        <CloseButton onClick={handleClose} aria-label="Close">
          <FontAwesomeIcon icon={faTimes} />
        </CloseButton>
        <Icon>
          <FontAwesomeIcon icon={faInfoCircle} />
        </Icon>
        <Title id="castle-closed-title">Castle Closed</Title>
        <Subtitle>
          Re-opens at Christmas ({CASTLE_CLOSED_UNTIL_CHRISTMAS_2026.reopenDate})
        </Subtitle>
        <OpenNotice>
          <strong>
            <FontAwesomeIcon icon={faTree} /> Gardens &amp; Playground are still open!
          </strong>
          <span>Open daily, 9:30am – 5:00pm. The Retail Village is open too.</span>
        </OpenNotice>
        <ActionLink to="/your-visit/opening-times" onClick={handleClose}>
          View Opening Times
        </ActionLink>
      </ModalContent>
    </ModalOverlay>
  );
};

export default CastleClosedPopup;
